import React, { useEffect } from 'react';
import { LayoutAnimation, StyleProp, ViewStyle } from 'react-native';
import { MBView } from '@shared/components/MBView';
import { MBAnimationBounceIn } from '@shared/components/animation';
import { MBIconV2 } from '@shared/components/image';
import { MBListRow, MBListRowProps } from '@shared/components/list/row';
import { Colors } from '@shared/styles';
import { usePrevious } from '@shared/util/hooks';

export interface MBSelectableRowProps extends MBListRowProps {
  /*
        Is the row currently selected
    */
  selected?: boolean;

  /*
        If set, the selectable dot will be shown as a
        radio button (filled circle). Otherwise a checkmark
        will be shown. Default is true.
    */
  radio?: boolean;

  radioStyle?: StyleProp<ViewStyle>;

  /*
        Callback for when the row is pressed and thus selected/deselected
    */
  onChange?: (selected: boolean) => void;
}

export function MBSelectableRow({
  selected,
  onChange,
  onPress,
  radio = true,
  radioStyle,
  ...otherProps
}: MBSelectableRowProps) {
  const handlePress = React.useCallback(
    (node: React.Component) => {
      onChange?.(!selected);
      onPress?.(node);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, onPress],
  );

  return (
    <MBListRow
      onPress={handlePress}
      icon={
        <MBView fill center style={radioStyle}>
          <MBSelectableDot selected={selected} radio={radio} />
        </MBView>
      }
      {...otherProps}
    />
  );
}

interface MBSelectableDotProps {
  selected?: boolean;
  radio?: boolean;
  size?: number;
  testID?: string;
}

/*
    A simple radio button dot with different appearance depending on if it's selected or not
*/
export function MBSelectableDot({ selected, size, radio = true, testID }: MBSelectableDotProps) {
  const prevSelected = usePrevious(selected);

  useEffect(() => {
    // When the selected state changes, do a layout animation. This will make
    // the old view fade out and the new view fade in, making the transition
    // to the next state smoother. To consider: by wrapping the below content
    // in a "bounce in" or similar view, we might make the transition even nicer.
    // Note: LayoutAnimations aren't available on web
    if (!!LayoutAnimation && selected !== prevSelected) {
      LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    }
  }, [selected, prevSelected]);

  if (selected && radio) {
    return (
      <MBAnimationBounceIn key="dot">
        <MBIconV2 color={Colors.v2.green} name="radio-button" size={size} testID={testID} />
      </MBAnimationBounceIn>
    );
  }

  if (selected && !radio) {
    return (
      <MBAnimationBounceIn key="checkmark">
        <MBIconV2 color={Colors.v2.green} name="task-filled" size={size} testID={testID} />
      </MBAnimationBounceIn>
    );
  }

  return <MBIconV2 color={Colors.v2.green30} name="unfilled-circle" size={size} testID={testID} />;
}
